<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { useAppStore } from "@/store/app";
import { PRODUCT_TYPE } from "@/util/constants";
import { XCloseIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIDrawer, UITextSmNormal } from "@gohighlevel/ghl-ui";
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import SidebarElements from "./SidebarElements.vue";

const { t } = useI18n();

const props = defineProps({
  builderType: {
    type: String,
    default: PRODUCT_TYPE.FORM,
  },
});
const show = ref(false);
const emitter = useEmitter();
const store = useAppStore();

const toggleOverlay = () => {
  show.value = !show.value;
  emitter.emit("close-email-side-bar");
};

const openLeftSidebar = () => {
  show.value = true;
};

const closeOverlay = () => {
  show.value = false;
};

// Handle sidebar state requests
const getSidebarState = (callback) => {
  if (typeof callback === "function") {
    callback({
      leftSidebarOpen: show.value,
      // Add other sidebar states
    });
  }
};

// Add direct state access for improved state management
const getLeftSidebarState = (callback) => {
  if (typeof callback === "function") {
    callback(show.value);
  }
};

// Close sidebar on request
const closeAllSidebars = () => {
  show.value = false;
};

// Restore sidebar state
const restoreSidebarState = (state) => {
  if (state && typeof state.leftSidebarOpen === "boolean") {
    show.value = state.leftSidebarOpen;
  }
};

const builderTypeConvert = computed(() => {
  if (props.builderType === PRODUCT_TYPE.FORM) {
    return t("form.formElement");
  }
  if (props.builderType === PRODUCT_TYPE.SURVEY) {
    return t("survey.surveyElement");
  }
  return t("quiz.quizElement");
});

onBeforeMount(() => {
  emitter.on("toggle-left-side-bar", toggleOverlay);
  emitter.on("close-left-side-bar", closeOverlay);
  emitter.on("open-left-side-bar", openLeftSidebar);

  // Add handlers for sidebar state management
  emitter.on("get-sidebar-state", getSidebarState);
  emitter.on("get-left-sidebar-state", getLeftSidebarState);
  emitter.on("close-all-sidebars", closeAllSidebars);
  emitter.on("restore-sidebar-state", restoreSidebarState);
});

onBeforeUnmount(() => {
  emitter.off("toggle-left-side-bar", toggleOverlay);
  emitter.off("close-left-side-bar", closeOverlay);
  emitter.off("open-left-side-bar", openLeftSidebar);

  // Clean up new handlers
  emitter.off("get-sidebar-state", getSidebarState);
  emitter.off("get-left-sidebar-state", getLeftSidebarState);
  emitter.off("close-all-sidebars", closeAllSidebars);
  emitter.off("restore-sidebar-state", restoreSidebarState);
});

onMounted(() => {
  show.value = true;
  store.leftSidebarOpen = show.value;
});

watch(show, (val) => {
  store.leftSidebarOpen = !!val;
});
</script>

<template>
  <div>
    <div>
      <UIDrawer
        id="hl-builder-element-layout"
        :show="show"
        :width="360"
        placement="left"
        :show-mask="false"
        to="#builder-inner-container"
        display-directive="show"
        class="border-gray-100"
      >
        <div class="flex w-full justify-between p-2">
          <UITextSmNormal>{{ builderTypeConvert }}</UITextSmNormal>
          <UIButton
            id="hl-builder-creator-header-close"
            :quaternary="true"
            text
            @click="closeOverlay"
          >
            <XCloseIcon class="h-5 w-5 text-gray-800" />
          </UIButton>
        </div>
        <SidebarElements></SidebarElements>
      </UIDrawer>
    </div>
  </div>
</template>

<style scoped>
.close-icon-element {
  right: 18px;
  top: 18px;
  position: absolute;
  cursor: pointer;
}
</style>
